import { HTMLAttributes, forwardRef } from 'react';
import './Stack.css';

export type StackDirection = 'horizontal' | 'vertical';
export type StackSpacing = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type StackAlign = 'start' | 'center' | 'end' | 'stretch';
export type StackJustify =
  | 'start'
  | 'center'
  | 'end'
  | 'between'
  | 'around'
  | 'evenly';

export interface StackProps extends HTMLAttributes<HTMLDivElement> {
  /** Direction of the stack */
  direction?: StackDirection;
  /** Spacing between items */
  spacing?: StackSpacing;
  /** Alignment of items on the cross axis */
  align?: StackAlign;
  /** Distribution of items on the main axis */
  justify?: StackJustify;
  /** Allow items to wrap */
  wrap?: boolean;
}

/**
 * Stack component for laying out items in a row or column.
 *
 * @example
 * ```tsx
 * <Stack direction="horizontal" spacing="md" align="center">
 *   <Button>One</Button>
 *   <Button>Two</Button>
 * </Stack>
 * ```
 */
export const Stack = forwardRef<HTMLDivElement, StackProps>(
  (
    {
      direction = 'vertical',
      spacing = 'md',
      align = 'stretch',
      justify = 'start',
      wrap = false,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const classNames = [
      'ui-stack',
      `ui-stack--${direction}`,
      `ui-stack--spacing-${spacing}`,
      `ui-stack--align-${align}`,
      `ui-stack--justify-${justify}`,
      wrap && 'ui-stack--wrap',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={classNames} {...props}>
        {children}
      </div>
    );
  }
);

Stack.displayName = 'Stack';
